import type { ReactNode } from "react";

import { Box } from "../../components/ui/box";
import {
  Meter,
  MeterIndicator,
  MeterLabel,
  MeterTrack,
  MeterValue,
} from "../../components/ui/meter";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Text } from "../../components/ui/text";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../components/ui/tooltip";
import { useElementSize } from "../../hooks/use-element-size";
import type { Omittable } from "../../lib/props";
import { useChartColors, type ChartColorToken } from "./colors";

/** Ink for a supporting figure. `muted` reads as a measure; the three state
 *  tones read as a count that should catch the eye. */
export type FunnelBarFigureTone = "muted" | "destructive" | "warning" | "success";

export type FunnelBarFigure = Omittable<{
  label: ReactNode;
  tone?: FunnelBarFigureTone;
  /** What the figure measures, shown on hover and focus. A derived number
   *  (an average, a share) is not self-explaining; a plain count usually is. */
  tooltip?: ReactNode;
}>;

export type FunnelBarItem = Omittable<{
  id: string;
  label: string;
  /** Row content in place of the plain text label. `label` still names the
   *  row for assistive tech, so keep the two in agreement. Content that is
   *  itself pressable cannot sit in a list that also takes `onItemClick`. */
  labelContent?: ReactNode;
  /** The band: how much is in this step. */
  value: number;
  valueLabel?: ReactNode;
  /** A part OF `value`, painted as a stronger band from the same edge — the
   *  share of the step that is in some state. Clamped to `value`. */
  partValue?: number;
  colorToken?: ChartColorToken;
  /** Supporting figures, printed right of the value in fixed columns: figure
   *  `i` lands in column `i`, so rows with fewer figures leave columns open
   *  and the figures still line up down the list. A figure wider than its
   *  column ellipsizes inside it — it never spills onto its neighbour — so a
   *  worded figure that keeps being cut short wants a wider `figureWidth`,
   *  not a shorter word on the busiest row. Order them by what a reader acts
   *  on: a list too narrow for every column drops them from the LAST one
   *  inward (see {@link FunnelBarList}). */
  figures?: readonly FunnelBarFigure[];
}>;

export type FunnelBarListProps = Omittable<{
  /** The steps, in the order the sequence runs. The list never sorts them. */
  items: readonly FunnelBarItem[];
  /**
   * Rows set apart under a hairline: where the sequence ENDS (the outcomes
   * work flows into). Unlike a ranked list's footer, a tail row IS a
   * magnitude — it keeps its wash and shares the ceiling, so an outcome that
   * outweighs every step is drawn that way.
   */
  tailItems?: readonly FunnelBarItem[];
  /** Accessible name for the sequence. */
  ariaLabel: string;
  /** Optional fixed comparison ceiling. Defaults to the largest item, tail included. */
  max?: number;
  /** Width of each figure column, in px. Figures right-align inside it and
   *  ellipsize at its edge. Raise it for a longer worded figure; the column is
   *  one width for the whole list, so size it for the widest row. */
  figureWidth?: number;
  /** How many figure columns to hold open. Defaults to the widest row's count.
   *  A ceiling: the list still drops columns it has no width for. */
  figureColumns?: number;
  /** Turns each row into a clickable drilldown target. */
  onItemClick?: (item: FunnelBarItem) => void;
}>;

/**
 * Vertical pitch of one row (its line box plus the list gap), in px. A consumer
 * that fits the list to a measured region passes this to
 * `useCategoryRowBudget` so the budget counts THESE rows, not a chart's. A
 * STACKED row (see {@link FunnelBarList}) is two lines and about twice this;
 * the list scrolls the difference.
 */
export const FUNNEL_BAR_ROW_HEIGHT = 30;

// The band is a wash BEHIND the row's label, not a mark beside it — the same
// reasoning as the ranked list: a small step degrades into a plain row instead
// of an empty rail. It runs in its own lane, left of the value and figures, so
// a number never sits on a band and the figures read on the card surface. The
// part band sits on the wash at a stronger alpha of the SAME hue, square at its
// data end so it reads as "this much of the step", never as a second pill.
const WASH_ALPHA_PERCENT = 20;
const PART_ALPHA_PERCENT = 38;

/** Width of the value column. Fixed and end-aligned so the counts line up. It
 *  holds six digits and a separator at the row's text size: a five-figure
 *  count is ordinary on a funnel read over a whole workspace. */
const VALUE_WIDTH = 56;
/** Width of a figure column. Sized for a WORDED figure ("906 overdue"), not a
 *  bare number — a figure names what it measures, and the word is the half a
 *  reader cannot infer from the column's position. */
const DEFAULT_FIGURE_WIDTH = 84;

/** The `sm` gap between the lane, the value and each figure cell, in px, and
 *  the row's trailing inset. Spelled out because the column budget below is
 *  arithmetic over them. */
const CELL_GAP = 8;
const ROW_TRAILING_INSET = 8;

/**
 * The narrowest lane the label and its band get before the list gives up a
 * figure column, in px, the lane's own 16px inset included. 80px of type is
 * "Estimate sent" whole and "Inspection Sch…" cut where a reader still knows
 * the stage; below it the label is a letter and the band behind it a sliver,
 * and both the name and the magnitude are gone — which is everything the row
 * says. The fixed cells on the right refuse to shrink, so without a floor here
 * every pixel the list loses comes out of the lane.
 */
const MIN_LANE_WIDTH = 96;

/**
 * What the fixed part of a one-line row spends with `columns` figure cells:
 * the value cell, the figure cells, the gap before each, and the trailing
 * inset.
 */
function fixedRowWidth(columns: number, figureWidth: number): number {
  return VALUE_WIDTH + columns * figureWidth + (columns + 1) * CELL_GAP + ROW_TRAILING_INSET;
}

/**
 * How many of `columns` figure cells the list can print on one line and still
 * leave the lane {@link MIN_LANE_WIDTH}; `null` where even the value alone
 * does not, and the row stacks. Unmeasured, the list prints every column it
 * was asked for.
 */
function fittingColumns(width: number, columns: number, figureWidth: number): number | null {
  if (width === 0) return columns;
  for (let shown = columns; shown >= 0; shown -= 1) {
    if (width - fixedRowWidth(shown, figureWidth) >= MIN_LANE_WIDTH) return shown;
  }
  return null;
}

function mix(color: string, alphaPercent: number): string {
  return `color-mix(in oklab, ${color} ${alphaPercent}%, transparent)`;
}

const FIGURE_TONE_VARIANT: Record<FunnelBarFigureTone, "muted" | "destructive" | "warning" | "success"> = {
  muted: "muted",
  destructive: "destructive",
  warning: "warning",
  success: "success",
};

/** The pressable shell every row shares: rounded and clipped, so a band cannot
 *  escape the corners, and a real button where the list drills down. */
function RowShell({
  item,
  onItemClick,
  children,
}: {
  item: FunnelBarItem;
  onItemClick?: ((item: FunnelBarItem) => void) | undefined;
  children: ReactNode;
}) {
  const pressProps = onItemClick
    ? {
        as: "button" as const,
        type: "button" as const,
        onClick: () => onItemClick(item),
      }
    : {};

  return (
    <Box
      {...pressProps}
      position="relative"
      width="100%"
      minWidth={0}
      rounded="md"
      overflow="hidden"
      hoverBackground={onItemClick ? true : undefined}
      cursor={onItemClick ? "pointer" : undefined}
      className={
        onItemClick
          ? "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          : undefined
      }
    >
      {children}
    </Box>
  );
}

/** The wash and, inside it, the part band. Absolute, so it paints under
 *  whatever the lane holds. */
function FunnelBands({
  color,
  percentage,
  partPercentage,
}: {
  color: string;
  percentage: number;
  partPercentage: number;
}) {
  return (
    <MeterTrack className="absolute inset-0 h-full bg-transparent">
      <MeterIndicator
        className="rounded-md"
        style={{
          width: `${percentage}%`,
          height: "100%",
          backgroundColor: mix(color, WASH_ALPHA_PERCENT),
        }}
      />
      {partPercentage > 0 ? (
        // A second indicator over the first: same hue, stronger alpha, square
        // data end, its own slot so it never reads as the meter's value.
        <MeterIndicator
          aria-hidden
          data-slot="funnel-part"
          className="absolute inset-y-0 left-0 rounded-l-md"
          style={{
            width: `${partPercentage}%`,
            backgroundColor: mix(color, PART_ALPHA_PERCENT),
          }}
        />
      ) : null}
    </MeterTrack>
  );
}

/** One supporting figure, with its tooltip where it has one. The figure
 *  itself is the trigger, so the row stays one button: a tooltip opens on
 *  hover, a press still drills. */
function FunnelFigure({ figure, fitted }: { figure: FunnelBarFigure; fitted: boolean }) {
  // The column's width is definite ON THE TEXT, not just on the cell around
  // it: a nowrap line's min-content IS its full width, so a cell that sizes
  // to its content grows past the column and the overflow paints over the
  // figure to its left. `w-full` ties the line to the column, which is what
  // turns an overlong figure into an ellipsis. A stacked figure has no column
  // and sizes to its text.
  const text = (
    <Text
      size="xs"
      variant={FIGURE_TONE_VARIANT[figure.tone ?? "muted"]}
      tabular
      truncate
      align="right"
      className={fitted ? "w-full" : undefined}
    >
      {figure.label}
    </Text>
  );
  if (!figure.tooltip) return text;
  return (
    <Tooltip>
      <TooltipTrigger render={text} />
      <TooltipPopup side="top">{figure.tooltip}</TooltipPopup>
    </Tooltip>
  );
}

function FunnelRow({
  item,
  ceiling,
  figureColumns,
  figureWidth,
  stacked,
  onItemClick,
}: {
  item: FunnelBarItem;
  ceiling: number;
  /** Figure cells on the line; `null` stacks the row (see {@link FunnelBarList}). */
  figureColumns: number | null;
  figureWidth: number;
  stacked: boolean;
  onItemClick?: ((item: FunnelBarItem) => void) | undefined;
}) {
  const c = useChartColors();
  const color = c.paint(item.colorToken);
  // Positive values keep a two-percent minimum mark so a small step stays visible.
  const percentage = item.value <= 0 ? 0 : Math.max(2, (item.value / ceiling) * 100);
  const part = Math.min(Math.max(item.partValue ?? 0, 0), Math.max(item.value, 0));
  const partPercentage = part <= 0 ? 0 : Math.max(2, (part / ceiling) * 100);
  const label = (
    <MeterLabel className="block min-w-0 truncate font-normal">
      {item.labelContent ?? item.label}
    </MeterLabel>
  );
  const value = (
    // `w-full` is what makes the clamp bind — see the figure cell.
    <MeterValue className="w-full truncate text-right font-semibold">
      {() => item.valueLabel ?? item.value.toLocaleString()}
    </MeterValue>
  );

  if (stacked) {
    return (
      <Box role="listitem">
        <RowShell item={item} onItemClick={onItemClick}>
          <Meter value={item.value} min={0} max={ceiling} className="gap-0">
            {/* Stacked, the lane IS the row: the band runs behind both lines,
                so it gets wider where the row got narrower — the right
                direction for a mark whose width is the datum. */}
            <Box position="relative" minWidth={0} rounded="md" overflow="hidden">
              <FunnelBands color={color} percentage={percentage} partPercentage={partPercentage} />
              <Box position="relative" direction="column" gap="2xs" paddingX="sm" paddingY="xs" minWidth={0}>
                {label}
                <Box direction="row" align="baseline" justify="between" gap="sm" minWidth={0}>
                  <Box width={VALUE_WIDTH} align="start" shrink={false}>
                    <MeterValue className="w-full truncate text-left font-semibold">
                      {() => item.valueLabel ?? item.value.toLocaleString()}
                    </MeterValue>
                  </Box>
                  <Box direction="row" align="baseline" justify="end" gap="sm" minWidth={0}>
                    {item.figures?.map((figure, index) => (
                      <FunnelFigure key={index} figure={figure} fitted={false} />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Meter>
        </RowShell>
      </Box>
    );
  }

  return (
    <Box role="listitem">
      <RowShell item={item} onItemClick={onItemClick}>
        <Meter value={item.value} min={0} max={ceiling} className="gap-0">
          <Box direction="row" align="center" gap="sm" paddingRight="sm" minWidth={0}>
            {/* The lane: the track fills it and the label sits on top, so the
              * bands paint under the name and stop where the figures begin. */}
            <Box position="relative" grow minWidth={0} rounded="md" overflow="hidden">
              <FunnelBands color={color} percentage={percentage} partPercentage={partPercentage} />
              <Box position="relative" paddingX="sm" paddingY="xs" minWidth={0}>
                {label}
              </Box>
            </Box>
            <Box width={VALUE_WIDTH} align="end" shrink={false}>
              {value}
            </Box>
            {Array.from({ length: figureColumns ?? 0 }, (_, index) => {
              const figure = item.figures?.[index];
              if (!figure) {
                return <Box key={index} width={figureWidth} shrink={false} />;
              }
              return (
                <Box key={index} width={figureWidth} align="end" shrink={false}>
                  <FunnelFigure figure={figure} fitted />
                </Box>
              );
            })}
          </Box>
        </Meter>
      </RowShell>
    </Box>
  );
}

/**
 * A sequence of steps read top to bottom, each one line — label, value, then a
 * fixed column per supporting figure — with the step's magnitude painted as a
 * proportional wash behind the line and, inside it, a stronger band for the
 * part of the step in some state. Rows keep the order they are handed; the form
 * is for a pipeline, a funnel, a checklist of stages, anywhere the ORDER is the
 * meaning and a ranking would destroy it.
 *
 * Where the steps end goes in `tailItems`: outcomes sit under a hairline, keep
 * their wash, and share the ceiling.
 *
 * Steps are usually entities that own a color; pass it as `colorToken`. Steps
 * with no color of their own should share ONE hue — the label already carries
 * identity, and a hue per row encodes nothing.
 *
 * The list measures ITSELF and fits the row to its width. The value and figure
 * cells are fixed so the columns line up down the list, which means a list
 * that loses width loses it from the label lane alone — three fixed cells on a
 * card a third of a board wide leave the lane one letter. So the lane has a
 * floor ({@link MIN_LANE_WIDTH}) and the figure columns are the give: the list
 * drops them from the last one inward until the lane has its floor, and where
 * even the value alone will not fit beside it, each row STACKS — the label on
 * one line, the value and every figure on the next, the band behind both. A
 * dropped figure is still in its tooltip's row for the reader who widens the
 * card; the caller decides nothing by its grid span, because a span says
 * nothing about pixels.
 */
export function FunnelBarList({
  items,
  tailItems,
  ariaLabel,
  max,
  figureWidth = DEFAULT_FIGURE_WIDTH,
  figureColumns,
  onItemClick,
}: FunnelBarListProps) {
  const { ref, size } = useElementSize<HTMLDivElement>();
  const tail = tailItems ?? [];
  if (items.length === 0 && tail.length === 0) return null;

  const all = [...items, ...tail];
  const ceiling = Math.max(max ?? 0, ...all.map((item) => item.value), 1);
  const columns = figureColumns ?? Math.max(0, ...all.map((item) => item.figures?.length ?? 0));
  const shown = fittingColumns(size.width, columns, figureWidth);
  const stacked = shown === null;

  return (
    <ScrollArea scrollFade>
      <Box ref={ref} direction="column" gap="2xs" role="list" aria-label={ariaLabel}>
        {items.map((item) => (
          <FunnelRow
            key={item.id}
            item={item}
            ceiling={ceiling}
            figureColumns={shown}
            figureWidth={figureWidth}
            stacked={stacked}
            onItemClick={onItemClick}
          />
        ))}
        {tail.length > 0 ? (
          // A hairline separates where the work went from the steps it went
          // through. The tail keeps the same columns, so the figures still
          // line up across the rule.
          <Box
            direction="column"
            gap="2xs"
            marginTop={items.length > 0 ? "2xs" : undefined}
            paddingTop={items.length > 0 ? "2xs" : undefined}
            border={items.length > 0 ? "top" : undefined}
          >
            {tail.map((item) => (
              <FunnelRow
                key={item.id}
                item={item}
                ceiling={ceiling}
                figureColumns={shown}
                figureWidth={figureWidth}
                stacked={stacked}
                onItemClick={onItemClick}
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </ScrollArea>
  );
}
